import * as React from "react";
import {Link} from "react-router-dom";
import {defaultDiscountCode, DiscountCode, OrderSearchParams} from "../../models/order";
import {ApplicationState} from "../../reducers";
import {connect} from "react-redux";
import {FormGroup, FormControl, ControlLabel, Col, Row, Button, Table, Modal} from "react-bootstrap";
import {DateRangePicker} from "react-dates";
import * as moment from "moment";
import {clone, range} from "lodash";

import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import {LOCAL_DATE_FMT, moneyFormat} from "../../utils";
import {
    deleteDiscount,
    DiscountsAction, getDiscountList, saveNewDiscount, updateDiscount,
    updateNewDiscount
} from "../../actions/discounts";
import {DiscountsState} from "../../reducers/discounts";
import {User} from "../../models/user";
import {LoadingOverlay} from "../Loading";

interface DiscountsProps extends DiscountsState {
    getDiscountList: () => Promise<DiscountsAction>;
    updateNewDiscount: (discount: DiscountCode) => DiscountsAction;
    saveNewDiscount: (discount: DiscountCode) => Promise<DiscountsAction>;
    updateDiscount: (discount: DiscountCode) => Promise<DiscountsAction>;
    deleteDiscount: (discount: DiscountCode) => Promise<DiscountsAction>;
}

class DiscountsGrid extends React.Component<DiscountsProps> {
    componentDidMount(): void {
        this.updateDiscountList();
    }

    updateDiscountList() {
        this.props.getDiscountList();
    }

    save() {
       const {newDiscount, saveNewDiscount, updateDiscount} = this.props;
       if (newDiscount && newDiscount.id) {
           updateDiscount(newDiscount);
       } else {
           saveNewDiscount(newDiscount);
       }
    }

    public render(): JSX.Element {
        let {discounts, newDiscount, loading, saveLoading, updateNewDiscount} = this.props;
        if (!discounts) {
            discounts = [];
        }
        if (loading) {
            return <LoadingOverlay/>;
        }

        return (
            <div>
                <DiscountEditModal close={() => updateNewDiscount(null)}
                                   discount={newDiscount}
                                   change={this.updateDiscount.bind(this)}
                                   save={this.save.bind(this)}
                                   isOpen={!!newDiscount}/>
                <Row>
                    <Col xs={12} className="text-right margin-bottom">
                        <Button bsStyle="success" onClick={() => updateNewDiscount(defaultDiscountCode())}>
                            <i className="fa fa-plus" /> Create Discount Code
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Table bordered striped hover>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Active From</th>
                                    <th>Active To</th>
                                    <th>Fixed Discount in $</th>
                                    <th>% off Order Subtotal</th>
                                    <th>Per Ticket</th>
                                    <th>Active</th>
                                    <th>Created On</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                            {discounts.map(discount => {
                                return (
                                    <tr key={discount.id}>
                                        <td>{discount.name}</td>
                                        <td>{moment(discount.activeFrom).format("LL")}</td>
                                        <td>{moment(discount.activeTo).format("LL")}</td>
                                        <td className="text-right">{discount.discountFixed !== null && moneyFormat(discount.discountFixed)}</td>
                                        <td className="text-right">{discount.discountPercent !== null && `${discount.discountPercent}%`}</td>
                                        <td>{discount.perTicket ? "Applied to Ticket" : "Applied to Order"}</td>
                                        <td>{discount.active ? "Active" : "Inactive"}</td>
                                        <td>{moment(discount.createdOn).format("LL")}</td>
                                        <td className="action-buttons">
                                            <Button className="hint--bottom"
                                                onClick={() => {updateNewDiscount(discount)}}
                                                aria-label="Edit Discount Code">
                                                <i className="fa fa-edit"  />
                                            </Button>
                                            <Button className="hint--bottom"
                                                    onClick={() => this.deleteDiscount(discount)}
                                                    aria-label="Delete Discount Code">
                                                <i className="fa fa-remove red" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </div>
        );
    }

    private updateDiscount(f: (s: DiscountCode) => void) {
        const s = clone(this.props.newDiscount);
        f(s);
        this.props.updateNewDiscount(s);
    }

    private deleteDiscount(d: DiscountCode): void {
       if (confirm(`Are you sure you want to delete ${d.name}?`)) {
           this.props.deleteDiscount(d);
       }
    }
}

const connected = connect((state: ApplicationState) => ({
    ...state.discounts
}), {
    getDiscountList,
    updateNewDiscount,
    saveNewDiscount,
    updateDiscount,
    deleteDiscount,
})(DiscountsGrid);

export default connected;

interface EditModalProps {
    discount: DiscountCode;
    change: (f: (discount: DiscountCode) => void) => void;
    save: (discount: DiscountCode) => void;
    close: () => void;
    isOpen: boolean;
}

interface EditModalState {
    focusedInput: any;
}

class DiscountEditModal extends React.Component<EditModalProps, EditModalState> {

    constructor(props: EditModalProps, context: any) {
        super(props, context);
        this.state = {focusedInput: null};
    }

    public render(): JSX.Element {
        const {isOpen, close, change, discount, save} = this.props;
        if (!discount) {
            return null;
        }
        const title = discount.id ? "Update" : "Create";
        return (
            <Modal show={isOpen} onHide={() => close()}>
                <Modal.Header>
                    <Modal.Title>{title} Discount Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{margin: "10px", clear: "both"}}>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Name*</ControlLabel>
                                    <FormControl type="text"
                                                 value={discount.name}
                                                 onChange={(e: any) =>
                                                     change((u: DiscountCode) => u.name = e.target.value)}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Active From - To</ControlLabel>
                                    <br/>
                                    <DateRangePicker
                                        showClearDates
                                        startDateId="activeFrom"
                                        endDateId="activeTo"
                                        enableOutsideDays={false}
                                        isOutsideRange={() => false}
                                        startDate={discount.activeFrom ? moment(discount.activeFrom) : null}
                                        endDate={discount.activeTo ? moment(discount.activeTo) : null}
                                        onDatesChange={evt => {
                                            change((s: DiscountCode) => {
                                                if (evt.startDate) {
                                                    s.activeFrom = evt.startDate.toISOString();
                                                } else {
                                                    s.activeFrom = null;
                                                }
                                                if (evt.endDate) {
                                                    s.activeTo = evt.endDate.toISOString();
                                                } else {
                                                    s.activeTo = null;
                                                }
                                            });
                                        }}
                                        focusedInput={this.state.focusedInput}
                                        onFocusChange={(focusedInput: any) => this.setState({focusedInput})}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Fixed Discount in $</ControlLabel>
                                    <FormControl type="number"
                                                 value={discount.discountFixed || ""}
                                                 onChange={(e: any) =>
                                                     change((u: DiscountCode) => u.discountFixed = parseFloat(e.target.value))}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>% off Order Subtotal</ControlLabel>
                                    <FormControl type="number"
                                                 disabled={discount.perTicket}
                                                 value={discount.discountPercent || ""}
                                                 onChange={(e: any) =>
                                                     change((u: DiscountCode) => u.discountPercent = parseFloat(e.target.value))}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Per Ticket</ControlLabel>
                                    <FormControl type="checkbox"
                                                 checked={discount.perTicket}
                                                 onChange={(e: any) =>
                                                     change((u: DiscountCode) => {
                                                         u.perTicket = e.target.checked;
                                                         if (u.perTicket) {
                                                             u.discountPercent = null;
                                                         }
                                                     })}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <ControlLabel>Is Active</ControlLabel>
                                    <FormControl type="checkbox"
                                                 checked={discount.active}
                                                 onChange={(e: any) =>
                                                     change((u: DiscountCode) => u.active = e.target.checked)}/>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={() => close()}>Cancel</Button>
                    <Button bsStyle="success" onClick={() => save(discount)}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/admin/Discounts.tsx